/**
 * 微信支付订单确认页（确认后跳转到班优的支付页面）
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { message } from "antd";
import { throttle } from "lodash";
import PayInfos from "../../components/payInfos";
import { getQuery } from "@/utils/navigator";
import getQueryString from "@/utils/getQueryString";
import { PayChannel, PaySubChannel, WxOrderInfo } from "@/types/payment";
import { postOrderPay, getWxOpenId } from "@/services/payment";
import { replaceUrlSearch, convertOrderStr2Obj } from "./utils";
import { MAX_PAY_TIME, PAY_ORDER_INFO } from "../const";

const MSG_ORDER_PAYING = "订单正在支付中";
const MSG_ORDER_PAY_FORBIDDEN = "订单不允许支付";
import "./index.less";
import { getSessionId } from "@/services/api_user";
import { useWxPay } from "@/hooks/useWxPay";

export interface PayContainerProps { }

const PayContainer: React.FC<PayContainerProps> = () => {
  const price: number = +getQuery("price") || 0; // 价格
  const orderId = getQuery("orderId");
  const account = getQuery("account");
  const description = getQuery("description");
  const duration = getQuery("expired");
  const code = getQuery("code");

  const maxMinutes = MAX_PAY_TIME;

  const [ifOutDate, setIfOutDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const checkOutDateRef = useRef<NodeJS.Timeout | null>(null);
  const errorMsgRef = useRef("");

  // 使用微信支付Hook
  const { paying, callWxPay } = useWxPay({
    onSuccess: () => {
      // 支付成功，跳转到成功页面
      const search = replaceUrlSearch(window.location.search);
      const successUrl = `${window.location.origin}/payment/payResult${search}&wcpayRequestMsg=ok&isFromPayPage=true`;
      console.log('支付成功，跳转到:', successUrl);
      window.location.replace(successUrl);
    },
    onCancel: () => {
      // 用户取消支付
      console.log('用户取消支付');
    },
    onError: (error) => {
      console.error('支付错误:', error);
    },
  });

  useEffect(() => {
    return () => {
      if (checkOutDateRef.current) clearTimeout(checkOutDateRef.current);
      errorMsgRef.current = "";
    };
  }, []);

  const loopCheckOutDate = useCallback(() => {
    if (checkOutDateRef.current) clearTimeout(checkOutDateRef.current);
    checkOutDateRef.current = setTimeout(() => {
      setIfOutDate(true);
    }, maxMinutes * 60 * 1000);
  }, [maxMinutes]);

  const createOrder = useCallback(
    throttle(async (openId: string) => {
      try {
        const res = await postOrderPay({
          orderId,
          payChannel: PayChannel.WECHATPAY,
          paySubChannel: PaySubChannel.WECHAT_JSAPI,
          openId,
        });
        console.log("postOrderPay", res);

        const {
          appid,
          noncestr,
          packageValue,
          paysign,
          signtype,
          timestamp,
          package: pkg,
        } = convertOrderStr2Obj(res.prepayInfo) as WxOrderInfo;

        // 调用微信支付
        await callWxPay({
          appid: appid,
          noncestr,
          packageValue,
          paysign,
          signtype,
          timestamp,
          package: pkg,
        });

        Cookies.set(
          PAY_ORDER_INFO,
          JSON.stringify({
            orderId: orderId,
            openId,
            orderInfo: {
              appid,
              noncestr,
              packageValue,
              paysign,
              signtype,
              timestamp,
              package: pkg,
            },
          }),
          {
            expires: 1,
          }
        );

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.log("createOrder error", error);
        // 订单正在支付中，从 Cookies 中取已有的支付信息直接重新调起微信支付
        if (error?.code === 40006111) {
          try {
            const payParams = Cookies.get(PAY_ORDER_INFO);
            if (payParams) {
              const { orderInfo } = JSON.parse(payParams);
              await callWxPay(orderInfo);
              return;
            }
          } catch (retryErr) {
            console.error("重试微信支付失败:", retryErr);
          }
          // 无可用缓存、解析失败或重试失败：统一提示
          message.warning(MSG_ORDER_PAYING);
          return;
        }
        // 订单已失效
        if (error?.code === 40006112) {
          setIfOutDate(true);
          return;
        }
        const errMsg = error?.message || "服务器异常，请稍后重试";
        errorMsgRef.current = errMsg;
        message.info(errMsg);
      }
    }, 1500),
    [callWxPay, orderId]
  );

  // 根据微信授权码获取openid
  const getOpenIdAndCreateOrder = useCallback(
    async (wxCode: string) => {
      try {
        setLoading(true);
        const sessionId = await getSessionId();
        if (sessionId == null) {
          message.warning("微信登录获取会话ID失败");
          return;
        }
        const openIdResult = await getWxOpenId(sessionId, wxCode);
        console.log("获取openid成功:", openIdResult);

        if (!openIdResult.openid) {
          throw new Error("获取微信用户信息失败，请重新扫码");
        }

        // 使用获取到的openid创建订单
        await createOrder(openIdResult.openid);
      } catch (err) {
        console.error("获取openid失败:", err);
        const errorMessage =
          err instanceof Error ? err.message : "获取微信用户信息失败，请重试";
        errorMsgRef.current = errorMessage;
        message.info(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [createOrder]
  );

  const btnPay = useCallback(() => {
    if (ifOutDate || paying) {
      return;
    }
    try {
      const payParams = Cookies.get(PAY_ORDER_INFO);
      console.log("btnPay", ifOutDate, payParams, errorMsgRef.current);
      if (payParams) {
        const payParamsObj = JSON.parse(payParams);
        if (payParamsObj.orderId === orderId) {
          console.log("btnPay", payParamsObj);
          createOrder(payParamsObj.openId);
        } else {
          message.warning("订单信息不匹配");
        }
      } else {
        message.warning(MSG_ORDER_PAY_FORBIDDEN);
        return;
      }
    } catch (err) {
      console.log("按钮支付出错了", err);
    }
  }, [ifOutDate, orderId, callWxPay, paying]);

  useEffect(() => {
    // 进入支付页先清掉旧订单缓存，避免与当前 orderId 混用
    Cookies.remove(PAY_ORDER_INFO);

    async function init() {
      try {
        if (
          getQueryString("wcpayRequestMsg") === "" &&
          getQuery("isFromPayPage") === "true"
        ) {
          loopCheckOutDate();
          const cleanedSearch = replaceUrlSearch(window.location.search);
          const newUrl = `${window.location.pathname}${cleanedSearch}`;
          window.history.replaceState({}, document.title, newUrl);
        } else {
          console.log("init WxCode", code);
          // 根据code码获取openId然后创建订单
          await getOpenIdAndCreateOrder(code);
          loopCheckOutDate();
        }
      } catch (err: unknown) {
        console.log("init error", err);
        const errorMessage = err instanceof Error ? err.message : "服务器异常，请稍后重试";
        message.info(errorMessage);
      }
    }
    init();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="paypage-container">
      <div className="paypage-price-container">
        <div className="price-title">需要支付金额</div>
        <div className="price">
          <span className="price-unit">￥</span>
          <span className="price-value">{price}</span>
        </div>
      </div>
      <PayInfos
        orderId={orderId}
        account={account}
        description={description}
        duration={duration}
      />
      <div className="paypage-bottom-container">
        <div
          className={`paypage-btn ${(ifOutDate || loading || paying) && "disabled"}`}
          onClick={btnPay}
        >
          <div>
            {paying
              ? "正在调起微信支付..."
              : loading
                ? "正在获取用户信息..."
                : ifOutDate
                  ? "二维码失效，请重新扫码"
                  : "立即支付"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayContainer;
